import { useRef, useState } from 'react'
import type { OpticalMeasureOverlayData } from '../../index'
import type { MeasurementFlowDebugScenario } from './debugScenarios'
import { useCameraMountAfterPaint } from './useCameraMountAfterPaint'

/**
 * A single activity state replaces the previous overlapping `isClosing` +
 * `reviewAction.isPending` flags:
 * - `idle`    — review controls are interactive.
 * - `pending` — a reversible action (retry) is in flight; returns to `idle`.
 * - `closing` — a terminal action (cancel/confirm) is tearing the flow down;
 *               sticky until unmount (confirm reverts to `idle` only on error).
 */
type FlowActivity = 'idle' | 'pending' | 'closing'

/**
 * Shared shell state for a measurement flow: the deferred camera mount, the
 * busy/teardown state machine, and the per-frame overlay + debug-scenario
 * handoffs. The three `run*` helpers wrap the review controls so guarding is
 * defined once here instead of being reassembled from ad-hoc flags in each
 * flow. Measurement-specific work (native commands, capture hooks) stays in
 * the flow and is injected as the async `action`.
 */
export function useMeasurementFlowSession<TViewState>() {
  const cameraReady = useCameraMountAfterPaint()
  const [overlayData, setOverlayData] = useState<OpticalMeasureOverlayData>()
  const [debugScenario, setDebugScenario] =
    useState<MeasurementFlowDebugScenario<TViewState> | null>(null)
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false)

  const activityRef = useRef<FlowActivity>('idle')
  const [activity, setActivityState] = useState<FlowActivity>('idle')
  // Read through a function so control-flow narrowing from an earlier guard
  // does not leak past `setActivity` (which mutates the ref opaquely).
  const getActivity = (): FlowActivity => activityRef.current
  const setActivity = (next: FlowActivity) => {
    activityRef.current = next
    setActivityState(next)
  }

  /** Retry: reversible, runs only from idle and always returns to idle. */
  const runRetry = async (action: () => Promise<void>) => {
    if (getActivity() !== 'idle') return
    setActivity('pending')
    try {
      await action()
    } finally {
      // A cancel can take over mid-retry; only the retry clears its own state.
      if (getActivity() === 'pending') setActivity('idle')
    }
  }

  /** Cancel: terminal, may pre-empt an in-flight retry, never reverts. */
  const runCancel = async (action: () => Promise<void>) => {
    if (getActivity() === 'closing') return
    setActivity('closing')
    await action()
  }

  /** Confirm: terminal, runs only from idle, reverts to idle if it throws. */
  const runConfirm = async (action: () => Promise<void>) => {
    if (getActivity() !== 'idle') return
    setActivity('closing')
    try {
      await action()
    } catch (error) {
      setActivity('idle')
      throw error
    }
  }

  return {
    cameraReady,
    /** Terminal teardown in progress — disables cancel controls. */
    isClosing: activity === 'closing',
    /** Any action in flight — disables retry/confirm controls. */
    isBusy: activity !== 'idle',
    overlayData,
    setOverlayData,
    debugScenario,
    setDebugScenario,
    /**
     * Instructions modal visibility. Each flow pauses its own capture while
     * this is open — the modal explains the measurement, so the user is
     * reading rather than posing.
     */
    isInstructionsOpen,
    setIsInstructionsOpen,
    runRetry,
    runCancel,
    runConfirm,
  }
}
